import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import ProductCard from "../components/ProductCard";
import QuickViewDialog from "../components/QuickViewDialog";
import PageBanner from "../components/PageBanner";
import { useProducts } from "../lib/ProductsContext";
import { useCart } from "../lib/CartContext";
import { emotions } from "../data/siteData";

// "En Çok Beğenilen" (rating) sıralaması kasıtlı olarak kaldırıldı — canlı
// ürünler için `rating` alanı her zaman null (backend'de ürün puanlama
// sistemi yok), bu seçenek seçildiğinde sessizce hiçbir şey yapmıyordu.
const SORTS = [
  { id: "featured", label: "Öne Çıkanlar" },
  { id: "price-asc", label: "Fiyat: Düşükten Yükseğe" },
  { id: "price-desc", label: "Fiyat: Yüksekten Düşüğe" },
];

export default function Shop() {
  const { products, categories } = useProducts();
  const { addToCart } = useCart();
  const [quickView, setQuickView] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "featured");

  const activeCat = searchParams.get("kategori") || "all";
  // "Gönderim Amacı" (Header'daki vesile menüsü) ile gelen filtre — panelde
  // ürüne eklenen occasionTags etiketlerine göre eşleşir (bkz. ProductsContext.jsx).
  const activeVesile = searchParams.get("vesile") || "";
  const activeVesileLabel = emotions.find((e) => e.id === activeVesile)?.label ?? null;

  const setActiveCat = (id) => {
    if (id === "all") {
      searchParams.delete("kategori");
    } else {
      searchParams.set("kategori", id);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const clearVesile = () => {
    searchParams.delete("vesile");
    setSearchParams(searchParams, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = products;
    if (activeCat !== "all") list = list.filter((p) => p.cat === activeCat);
    if (activeVesile) list = list.filter((p) => p.occasionTags?.includes(activeVesile));
    if (query.trim()) {
      const q = query.trim().toLocaleLowerCase("tr-TR");
      list = list.filter((p) => p.name.toLocaleLowerCase("tr-TR").includes(q));
    }
    const sorted = [...list];
    if (sort === "price-asc") sorted.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") sorted.sort((a, b) => b.price - a.price);
    return sorted;
  }, [activeCat, activeVesile, query, sort, products]);

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="shop-page">
      <Header />

      <PageBanner
        eyebrow="Vitrin"
        title="Tüm Ürünler"
        subtitle="Premium buketlerden orkidelere, kutuda güllerden çikolatalı hediyelere kadar tüm koleksiyonumuz."
        breadcrumb={[{ label: "Ürünler" }]}
      />

      <section className="py-14 md:py-20 bg-white">
        <div className="frida-container">
          {/* Filters */}
          <div className="flex flex-col gap-5 mb-10">
            <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-[#888888] absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ürün ara..."
                  className="w-full pl-11 pr-4 h-12 rounded-full border border-[#EAEAEA] bg-[#F8F8F8] outline-none focus:border-[#A85C34] transition-colors text-sm"
                  data-testid="shop-search-input"
                />
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <SlidersHorizontal className="w-4 h-4 text-[#888888]" />
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="h-12 rounded-full border border-[#EAEAEA] bg-white px-4 text-sm font-medium outline-none focus:border-[#A85C34] transition-colors"
                  data-testid="shop-sort-select"
                >
                  {SORTS.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="shop-category-filters">
              <button
                onClick={() => setActiveCat("all")}
                className={`px-4 py-2.5 text-sm font-medium rounded-full whitespace-nowrap transition-all border ${
                  activeCat === "all"
                    ? "bg-[#A85C34] text-white border-[#A85C34]"
                    : "border-[#EAEAEA] text-[#555555] hover:border-[#A85C34] hover:text-[#A85C34]"
                }`}
              >
                Tümü
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveCat(c.id)}
                  className={`px-4 py-2.5 text-sm font-medium rounded-full whitespace-nowrap transition-all border ${
                    activeCat === c.id
                      ? "bg-[#A85C34] text-white border-[#A85C34]"
                      : "border-[#EAEAEA] text-[#555555] hover:border-[#A85C34] hover:text-[#A85C34]"
                  }`}
                  data-testid={`shop-category-${c.id}`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            {activeVesileLabel && (
              <div className="flex items-center gap-2" data-testid="shop-vesile-chip">
                <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium bg-[#F5E9DD] text-[#A85C34]">
                  Gönderim Amacı: {activeVesileLabel}
                  <button onClick={clearVesile} aria-label="Vesile filtresini kaldır" className="hover:text-[#8B4726]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              </div>
            )}

            {(activeCat !== "all" || query || activeVesile) && (
              <div className="flex items-center gap-2 text-sm text-[#555555]">
                <span>{filtered.length} sonuç bulundu</span>
                <button
                  onClick={() => {
                    setQuery("");
                    setActiveCat("all");
                    clearVesile();
                  }}
                  className="inline-flex items-center gap-1 text-[#A85C34] font-medium hover:underline"
                >
                  <X className="w-3.5 h-3.5" />
                  Filtreleri temizle
                </button>
              </div>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-24" data-testid="shop-empty">
              <p className="text-[#555555] text-lg">Aramanızla eşleşen ürün bulunamadı.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8" data-testid="shop-grid">
              {filtered.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAddToCart={addToCart}
                  onQuickView={setQuickView}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingButtons />

      <QuickViewDialog
        product={quickView}
        onClose={() => setQuickView(null)}
        onAddToCart={addToCart}
      />
    </div>
  );
}
